import { isAbortOrCanceledError } from "@mirage-web/utils/abort-signals";

import { ApiHost, applyCapSrvHeaders } from "./api-client";
import { NetworkingLayerConfig } from "./config";
import { ApiUnavailableError } from "./known-api-error";
import { waitBeforeNetworkRetry } from "./network-retry";
import { replacePathParams } from "./path-params";

export class SSEStreamReadError extends Error {
  constructor(cause: unknown) {
    super("SSE stream connection was interrupted", { cause });
    this.name = "SSEStreamReadError";
  }
}

export interface SSEEvent {
  id?: string;
  event?: string;
  data: string;
  retry?: number;
}

export type SSEConnectionState = "idle" | "connecting" | "connected" | "closed";

export interface SSEStreamConnection {
  readonly state: SSEConnectionState;
  connect: () => Promise<void>;
  disconnect: () => void;
}

interface SSEConnectOptions {
  host: ApiHost;
  path: string;
  pathParams?: Record<string, string>;
  params?: Record<string, unknown>;
  headers?: Record<string, string>;
  signal?: AbortSignal;
  lastEventId?: string;
  onOpen?: () => void;
  onClose?: () => void;
  onEvent: (event: SSEEvent) => void;
}

interface ParsedSSEBuffer {
  events: SSEEvent[];
  remainder: string;
}

const EVENT_DELIMITER = /\r\n\r\n|\n\n/;

export function createSSEStreamConnection(
  config: NetworkingLayerConfig,
  options: SSEConnectOptions
): SSEStreamConnection {
  let state: SSEConnectionState = "idle";
  let connectPromise: Promise<void> | undefined;
  let abortController: AbortController | undefined;
  let didDisconnect = false;
  let removeAbortListener: (() => void) | undefined;

  function disconnect() {
    didDisconnect = true;
    abortController?.abort();
  }

  function getState() {
    return state;
  }

  async function connect() {
    if (connectPromise) {
      return connectPromise;
    }

    didDisconnect = false;

    connectPromise = (async () => {
      state = "connecting";
      abortController = new AbortController();

      if (options.signal) {
        const handleAbort = () => {
          abortController?.abort();
        };
        options.signal.addEventListener("abort", handleAbort);
        removeAbortListener = () => options.signal?.removeEventListener("abort", handleAbort);
        if (options.signal.aborted) {
          handleAbort();
        }
      }

      try {
        await waitBeforeNetworkRetry(abortController.signal);
        abortController.signal.throwIfAborted();
        const url = makeSSEUrl(
          config,
          options.host,
          replacePathParams(options.path, options.pathParams),
          options.params
        );
        const { accessToken, headers } = await makeSSEHeaders(config, options);
        abortController.signal.throwIfAborted();
        let response = await fetch(url, {
          method: "GET",
          headers,
          signal: abortController.signal,
        });

        if (response.status === 401 && accessToken) {
          const refreshedAccessToken = await config.getAccessToken({
            forceRefresh: true,
            staleAccessToken: accessToken,
          });
          abortController.signal.throwIfAborted();
          if (refreshedAccessToken && refreshedAccessToken !== accessToken) {
            response = await fetch(url, {
              method: "GET",
              headers: {
                ...headers,
                Authorization: `Bearer ${refreshedAccessToken}`,
              },
              signal: abortController.signal,
            });
          }
        }

        if (!response.ok) {
          throw new ApiUnavailableError(response.status, null);
        }
        if (!response.body) {
          throw new Error("SSE response did not include a stream body");
        }

        state = "connected";
        options.onOpen?.();

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let rawBuffer = "";
        let readerDone = false;

        while (!readerDone) {
          const { done, value } = await reader.read().catch((error: unknown) => {
            if (isAbortOrCanceledError(error)) {
              throw error;
            }
            throw new SSEStreamReadError(error);
          });
          if (done) {
            readerDone = true;
            continue;
          }

          rawBuffer += decoder.decode(value, { stream: true });
          const { events, remainder } = parseSSEBuffer(rawBuffer);
          rawBuffer = remainder;

          for (const event of events) {
            options.onEvent(event);
          }
        }
      } catch (error) {
        if (didDisconnect || options.signal?.aborted || isAbortOrCanceledError(error)) {
          return;
        }

        throw error;
      } finally {
        removeAbortListener?.();
        removeAbortListener = undefined;
        abortController = undefined;
        state = "closed";
        options.onClose?.();
      }
    })().finally(() => {
      connectPromise = undefined;
    });

    return connectPromise;
  }

  return {
    get state() {
      return getState();
    },
    connect,
    disconnect,
  };
}

function parseSSEBuffer(buffer: string): ParsedSSEBuffer {
  const events: SSEEvent[] = [];
  let remainder = buffer;

  let match = EVENT_DELIMITER.exec(remainder);
  while (match) {
    const block = remainder.slice(0, match.index);
    remainder = remainder.slice(match.index + match[0].length);

    const parsedEvent = parseSSEBlock(block);
    if (parsedEvent) {
      events.push(parsedEvent);
    }

    match = EVENT_DELIMITER.exec(remainder);
  }

  return { events, remainder };
}

function parseSSEBlock(block: string): SSEEvent | null {
  const lines = block.split(/\r?\n/);
  const dataLines: string[] = [];
  let id: string | undefined;
  let eventName: string | undefined;
  let retry: number | undefined;

  for (const line of lines) {
    if (!line || line.startsWith(":")) {
      continue;
    }

    const separatorIndex = line.indexOf(":");
    const field = separatorIndex === -1 ? line : line.slice(0, separatorIndex);
    let value = separatorIndex === -1 ? "" : line.slice(separatorIndex + 1);

    if (value.startsWith(" ")) {
      value = value.slice(1);
    }

    switch (field) {
      case "event":
        eventName = value;
        break;
      case "data":
        dataLines.push(value);
        break;
      case "id":
        if (!value.includes("\u0000")) {
          id = value;
        }
        break;
      case "retry": {
        const parsedRetry = Number.parseInt(value, 10);
        if (!Number.isNaN(parsedRetry)) {
          retry = parsedRetry;
        }
        break;
      }
      default:
        break;
    }
  }

  if (dataLines.length === 0) {
    return null;
  }

  return {
    id,
    event: eventName,
    data: dataLines.join("\n"),
    retry,
  };
}

function makeSSEUrl(
  config: NetworkingLayerConfig,
  host: ApiHost,
  path: string,
  params: Record<string, unknown> = {}
) {
  const urlHost = typeof host === "object" ? host.host : config.getApiHost(host);
  const url = new URL(path, urlHost);

  for (const [key, value] of Object.entries(params)) {
    if (typeof value === "undefined") {
      continue;
    }
    if (Array.isArray(value)) {
      for (const item of value) {
        url.searchParams.append(key, String(item));
      }
      continue;
    }
    url.searchParams.append(key, String(value));
  }

  return url;
}

async function makeSSEHeaders(config: NetworkingLayerConfig, options: SSEConnectOptions) {
  const configHeaders =
    typeof config.headers === "function"
      ? config.headers(options.host, { path: options.path, method: "GET" })
      : config.headers;

  const headers: Record<string, string> = {
    ...options.headers,
    ...configHeaders,
    Accept: "text/event-stream",
  };

  const accessToken = await config.getAccessToken();
  if (accessToken) {
    headers.Authorization = `Bearer ${accessToken}`;
  }

  if (options.host === "cap-srv") {
    applyCapSrvHeaders(headers);
  }

  if (options.lastEventId) {
    headers["Last-Event-ID"] = options.lastEventId;
  }

  return { accessToken, headers };
}
